import { FC } from 'react';

const Section: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-3">
    <h2 className="font-newsreader font-bold text-xl text-[rgba(0,0,0,0.87)]">{title}</h2>
    {children}
  </section>
);

const Sub: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="font-dm font-semibold text-base text-[rgba(0,0,0,0.87)]">{title}</h3>
    {children}
  </div>
);

const P: FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="font-dm text-sm leading-[175%] text-[rgba(0,0,0,0.7)]">{children}</p>
);

const Ul: FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc list-outside pl-5 flex flex-col gap-1">
    {items.map((item, i) => (
      <li key={i} className="font-dm text-sm leading-[175%] text-[rgba(0,0,0,0.7)]">
        {item}
      </li>
    ))}
  </ul>
);

const PrivacyPolicyPage: FC = () => {
  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-3xl mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="font-newsreader font-bold text-3xl text-[rgba(0,0,0,0.87)]">Privacy Policy</h1>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.5)]">Last updated: May 11, 2026</p>
        </div>

        <P>
          This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your
          information when You use the Service and tells You about Your privacy rights and how the law protects You.
        </P>
        <P>
          We use Your Personal Data to provide and improve the Service. By using the Service, You agree to the
          collection and use of information in accordance with this Privacy Policy.
        </P>

        <Section title="Interpretation and Definitions">
          <Sub title="Interpretation">
            <P>
              The words whose initial letters are capitalized have meanings defined under the following conditions. The
              following definitions shall have the same meaning regardless of whether they appear in singular or in
              plural.
            </P>
          </Sub>
          <Sub title="Definitions">
            <P>For the purposes of this Privacy Policy:</P>
            <Ul
              items={[
                'Account means a unique account created for You to access our Service or parts of our Service.',
                'Affiliate means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.',
                'Business, for the purpose of CCPA/CPRA, refers to the Company as the legal entity that collects Consumers\' personal information and determines the purposes and means of the processing of Consumers\' personal information, or on behalf of which such information is collected and that alone, or jointly with others, determines the purposes and means of the processing of consumers\' personal information, that does business in the State of California.',
                'CCPA and/or CPRA refers to the California Consumer Privacy Act (the "CCPA") as amended by the California Privacy Rights Act of 2020 (the "CPRA").',
                'Company (referred to as either "the Company", "We", "Us" or "Our" in this Privacy Policy) refers to Zero Degree Enterprises, Inc., 1 Diamond Avenue, Dayton, OH 45403. For the purposes of the GDPR, the Company is the Data Controller.',
                'Consumer, for the purpose of the CCPA/CPRA, means a natural person who is a California resident. A resident, as defined in the law, includes (1) every individual who is in the USA for other than a temporary or transitory purpose, and (2) every individual who is domiciled in the USA who is outside the USA for a temporary or transitory purpose.',
                'Cookies are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.',
                'Country refers to: Delaware, United States',
                'Data Controller, for the purposes of the GDPR (General Data Protection Regulation), refers to the Company as the legal person which alone or jointly with others determines the purposes and means of the processing of Personal Data.',
                'Device means any device that can access the Service such as a computer, a cell phone or a digital tablet.',
                'Do Not Track (DNT) is a concept that has been promoted by US regulatory authorities, in particular the U.S. Federal Trade Commission (FTC), for the Internet industry to develop and implement a mechanism for allowing internet users to control the tracking of their online activities across websites.',
                'GDPR refers to EU General Data Protection Regulation.',
                'Personal Data (or "Personal Information") is any information that relates to an identified or identifiable individual. For the purposes of GDPR, Personal Data means any information relating to You such as a name, an identification number, location data, online identifier or to one or more factors specific to the physical, physiological, genetic, mental, economic, cultural or social identity. For the purposes of the CCPA/CPRA, Personal Data means any information that identifies, relates to, describes or is capable of being associated with, or could reasonably be linked, directly or indirectly, with You. We use "Personal Data" and "Personal Information" interchangeably unless a law uses a specific term.',
                'Service refers to the Website.',
                'Service Provider means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used. For the purposes of the GDPR, Service Providers are considered Data Processors.',
                'Usage Data refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).',
                'Website refers to COMMAND - Execution Governance Infrastructure, accessible from http://www.zdecommand.com.',
                'You means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable. Under GDPR, You can be referred to as the Data Subject or as the User as you are the individual using the Service.',
              ]}
            />
          </Sub>
        </Section>

        <Section title="Collecting and Using Your Personal Data">
          <Sub title="Types of Data Collected">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <h4 className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Personal Data</h4>
                <P>
                  While using Our Service, We may ask You to provide Us with certain personally identifiable information
                  that can be used to contact or identify You. Personally identifiable information may include, but is
                  not limited to:
                </P>
                <Ul items={['Email address', 'First name and last name']} />
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Usage Data</h4>
                <P>Usage Data is collected automatically when using the Service.</P>
                <P>
                  Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address),
                  browser type, browser version, the pages of our Service that You visit, the time and date of Your
                  visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                </P>
                <P>
                  When You access the Service by or through a mobile device, We may collect certain information
                  automatically, including, but not limited to, the type of mobile device You use, Your mobile device's
                  unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile
                  Internet browser You use, unique device identifiers and other diagnostic data.
                </P>
                <P>
                  We may also collect information that Your browser sends whenever You visit Our Service or when You
                  access the Service by or through a mobile device.
                </P>
              </div>
            </div>
          </Sub>

          <Sub title="Tracking Technologies and Cookies">
            <P>
              We use Cookies and similar tracking technologies to track the activity on Our Service and store certain
              information. Tracking technologies We use include beacons, tags, and scripts to collect and track
              information and to improve and analyze Our Service. The technologies We use may include:
            </P>
            <Ul
              items={[
                'Cookies or Browser Cookies. A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service.',
                'Web Beacons. Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).',
              ]}
            />
            <P>
              Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your personal computer or
              mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web
              browser.
            </P>
            <P>
              Where required by law, we use non-essential cookies (such as analytics, advertising, and remarketing
              cookies) only with Your consent. You can withdraw or change Your consent at any time using Our cookie
              preferences tool (if available) or through Your browser/device settings. Withdrawing consent does not
              affect the lawfulness of processing based on consent before its withdrawal.
            </P>
            <P>We use both Session and Persistent Cookies for the purposes set out below:</P>
            {[
              {
                name: 'Necessary / Essential Cookies',
                type: 'Session Cookies',
                admin: 'Us',
                purpose:
                  'These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that You have asked for cannot be provided, and We only use these Cookies to provide You with those services.',
              },
              {
                name: 'Cookies Policy / Notice Acceptance Cookies',
                type: 'Persistent Cookies',
                admin: 'Us',
                purpose: 'These Cookies identify if users have accepted the use of cookies on the Website.',
              },
              {
                name: 'Functionality Cookies',
                type: 'Persistent Cookies',
                admin: 'Us',
                purpose:
                  'These Cookies allow Us to remember choices You make when You use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide You with a more personal experience and to avoid You having to re-enter your preferences every time You use the Website.',
              },
              {
                name: 'Tracking and Performance Cookies',
                type: 'Persistent Cookies',
                admin: 'Third Parties',
                purpose:
                  'These Cookies are used to track information about traffic to the Website and how users use the Website. The information gathered via these Cookies may directly or indirectly identify you as an individual visitor. This is because the information collected is typically linked to a pseudonymous identifier associated with the device you use to access the Website. We may also use these Cookies to test new pages, features or functionality of the Website to see how our users react to them.',
              },
            ].map((cookie) => (
              <div key={cookie.name} className="flex flex-col gap-1 pl-4 border-l-2 border-gray-200">
                <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">{cookie.name}</p>
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
                  <span className="font-medium">Type:</span> {cookie.type}
                </p>
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
                  <span className="font-medium">Administered by:</span> {cookie.admin}
                </p>
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
                  <span className="font-medium">Purpose:</span> {cookie.purpose}
                </p>
              </div>
            ))}
          </Sub>

          <Sub title="Use of Your Personal Data">
            <P>The Company may use Personal Data for the following purposes:</P>
            <Ul
              items={[
                'To provide and maintain our Service, including to monitor the usage of our Service.',
                'To manage Your Account: to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.',
                'For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.',
                'To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application\'s push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.',
                'To provide You with news, special offers, and general information about other goods, services and events which We offer that are similar to those that you have already purchased or inquired about unless You have opted not to receive such information.',
                'To manage Your requests: To attend and manage Your requests to Us.',
                'For business transfers: We may use Your Personal Data to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.',
                'For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.',
              ]}
            />
            <P>We may share Your Personal Data in the following situations:</P>
            <Ul
              items={[
                'With Service Providers: We may share Your Personal Data with Service Providers to monitor and analyze the use of our Service, to contact You.',
                'For business transfers: We may share or transfer Your Personal Data in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.',
                'With Affiliates: We may share Your Personal Data with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.',
                'With business partners: We may share Your Personal Data with Our business partners to offer You certain products, services or promotions.',
                'With other users: If Our Service offers public areas, when You share Personal Data or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.',
                'With Your consent: We may disclose Your Personal Data for any other purpose with Your consent.',
              ]}
            />
          </Sub>

          <Sub title="Retention of Your Personal Data">
            <P>
              The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in
              this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our
              legal obligations (for example, if We are required to retain Your data to comply with applicable laws),
              resolve disputes, and enforce our legal agreements and policies.
            </P>
            <P>
              Where possible, We apply shorter retention periods and/or reduce identifiability by deleting, aggregating,
              or anonymizing data. Unless otherwise stated, the retention periods below are maximum periods ("up to")
              and We may delete or anonymize data sooner when it is no longer needed for the relevant purpose. We apply
              different retention periods to different categories of Personal Data based on the purpose of processing
              and legal obligations:
            </P>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Account Information</p>
                <Ul items={['User Accounts: retained for the duration of your account relationship plus up to 24 months after account closure to handle any post-termination issues or resolve disputes.']} />
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Customer Support Data</p>
                <Ul
                  items={[
                    'Support tickets and correspondence: up to 24 months from the date of ticket closure to resolve follow-up inquiries, track service quality, and defend against potential legal claims.',
                    'Chat transcripts: up to 24 months for quality assurance and staff training purposes.',
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Usage Data</p>
                <P>
                  Website analytics data (cookies, IP addresses, device identifiers): up to 24 months from the date of
                  collection, which allows us to analyze trends while respecting privacy principles.
                </P>
                <P>
                  Server logs (IP addresses, access times): up to 24 months for security monitoring and
                  troubleshooting purposes.
                </P>
                <P>
                  Usage Data is retained in accordance with the retention periods described above, and may be retained
                  longer only where necessary for security, fraud prevention, or legal compliance.
                </P>
              </div>
            </div>
            <P>We may retain Personal Data beyond the periods stated above for different reasons:</P>
            <Ul
              items={[
                'Legal obligation: We are required by law to retain specific data (e.g., financial records for tax authorities).',
                'Legal claims: Data is necessary to establish, exercise, or defend legal claims.',
                'Your explicit request: You ask Us to retain specific information.',
                'Technical limitations: Data exists in backup systems that are scheduled for routine deletion.',
              ]}
            />
            <P>
              You may request information about how long We will retain Your Personal Data by contacting Us.
            </P>
            <P>When retention periods expire, We securely delete or anonymize Personal Data according to the following procedures:</P>
            <Ul
              items={[
                'Deletion: Personal Data is removed from Our systems and no longer actively processed.',
                'Backup retention: Residual copies may remain in encrypted backups for a limited period consistent with our backup retention schedule and are not restored except where necessary for security, disaster recovery, or legal compliance.',
                'Anonymization: In some cases, We convert Personal Data into anonymous statistical data that cannot be linked back to You. This anonymized data may be retained indefinitely for research and analytics.',
              ]}
            />
          </Sub>

          <Sub title="Transfer of Your Personal Data">
            <P>
              Your information, including Personal Data, is processed at the Company's operating offices and in any
              other places where the parties involved in the processing are located. It means that this information may
              be transferred to — and maintained on — computers located outside of Your state, province, country or
              other governmental jurisdiction where the data protection laws may differ from those from Your
              jurisdiction.
            </P>
            <P>
              Where required by applicable law, We will ensure that international transfers of Your Personal Data are
              subject to appropriate safeguards and supplementary measures where appropriate. The Company will take all
              steps reasonably necessary to ensure that Your data is treated securely and in accordance with this
              Privacy Policy and no transfer of Your Personal Data will take place to an organization or a country
              unless there are adequate controls in place including the security of Your data and other personal
              information.
            </P>
          </Sub>

          <Sub title="Delete Your Personal Data">
            <P>
              You have the right to delete or request that We assist in deleting the Personal Data that We have
              collected about You.
            </P>
            <P>
              Our Service may give You the ability to delete certain information about You from within the Service.
            </P>
            <P>
              You may update, amend, or delete Your information at any time by signing in to Your Account, if you have
              one, and visiting the account settings section that allows you to manage Your personal information. You
              may also contact Us to request access to, correct, or delete any Personal Data that You have provided to
              Us.
            </P>
            <P>
              Please note, however, that We may need to retain certain information when we have a legal obligation or
              lawful basis to do so.
            </P>
          </Sub>
        </Section>

        <Section title="Disclosure of Your Personal Data">
          <Sub title="Business Transactions">
            <P>
              If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred.
              We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy
              Policy.
            </P>
          </Sub>
          <Sub title="Law enforcement">
            <P>
              Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do
              so by law or in response to valid requests by public authorities (e.g. a court or a government agency).
            </P>
          </Sub>
          <Sub title="Other legal requirements">
            <P>
              The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:
            </P>
            <Ul
              items={[
                'Comply with a legal obligation',
                'Protect and defend the rights or property of the Company',
                'Prevent or investigate possible wrongdoing in connection with the Service',
                'Protect the personal safety of Users of the Service or the public',
                'Protect against legal liability',
              ]}
            />
          </Sub>
        </Section>

        <Section title="Security of Your Personal Data">
          <P>
            The security of Your Personal Data is important to Us, but remember that no method of transmission over the
            Internet, or method of electronic storage is 100% secure. While We strive to use commercially reasonable
            means to protect Your Personal Data, We cannot guarantee its absolute security.
          </P>
        </Section>

        <Section title="Detailed Information on the Processing of Your Personal Data">
          <P>
            The Service Providers We use may have access to Your Personal Data. These third-party vendors collect,
            store, use, process and transfer information about Your activity on Our Service in accordance with their
            Privacy Policies.
          </P>
          <Sub title="Analytics">
            <P>We may use third-party Service Providers to monitor and analyze the use of our Service.</P>
            <div className="flex flex-col gap-2">
              <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">Google Analytics</p>
              <P>
                Google Analytics is a web analytics service offered by Google that tracks and reports website traffic.
                Google uses the data collected to track and monitor the use of our Service. This data is shared with
                other Google services. Google may use the collected data to contextualize and personalize the ads of
                its own advertising network.
              </P>
              <P>
                You can opt-out of having made your activity on the Service available to Google Analytics by installing
                the Google Analytics opt-out browser add-on. The add-on prevents the Google Analytics JavaScript
                (ga.js, analytics.js and dc.js) from sharing information with Google Analytics about visits activity.
              </P>
              <P>
                For more information on the privacy practices of Google, please visit the Google Privacy &amp; Terms
                web page: https://policies.google.com/privacy
              </P>
            </div>
          </Sub>
        </Section>

        <Section title="GDPR Privacy">
          <Sub title="Legal Basis for Processing Personal Data under GDPR">
            <P>We may process Personal Data under the following conditions:</P>
            <Ul
              items={[
                'Consent: You have given Your consent for processing Personal Data for one or more specific purposes.',
                'Performance of a contract: Provision of Personal Data is necessary for the performance of an agreement with You and/or for any pre-contractual obligations thereof.',
                'Legal obligations: Processing Personal Data is necessary for compliance with a legal obligation to which the Company is subject.',
                'Vital interests: Processing Personal Data is necessary in order to protect Your vital interests or of another natural person.',
                'Public interests: Processing Personal Data is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Company.',
                'Legitimate interests: Processing Personal Data is necessary for the purposes of the legitimate interests pursued by the Company.',
              ]}
            />
            <P>
              In any case, the Company will gladly help to clarify the specific legal basis that applies to the
              processing, and in particular whether the provision of Personal Data is a statutory or contractual
              requirement, or a requirement necessary to enter into a contract.
            </P>
          </Sub>

          <Sub title="International Transfer of Personal Data">
            <P>
              We may transfer, store, and process Personal Data in countries other than the country in which You are
              located, including countries outside the European Economic Area ("EEA") and the United Kingdom ("UK"),
              where data protection laws may differ.
            </P>
            <P>
              Where we transfer Personal Data outside the EEA/UK to a country that has not been recognized as
              providing an adequate level of protection, We rely on appropriate safeguards, such as:
            </P>
            <Ul
              items={[
                "The European Commission's Standard Contractual Clauses (\"SCCs\") and/or the UK International Data Transfer Agreement (\"IDTA\") or the UK Addendum to the SCCs (as applicable)",
                'Supplementary measures where appropriate, such as encryption in transit and at rest, access controls, data minimisation, and vendor security reviews.',
              ]}
            />
            <P>
              We transfer Personal Data internationally only as needed to provide the Service and to work with our
              Service Providers (for example, hosting, analytics, email delivery). You may contact Us using the details
              in the "Contact Us" section of Our Privacy Policy to request further information about the safeguards We
              use for international transfers, including copies of relevant contractual protections (redacted where
              necessary).
            </P>
          </Sub>

          <Sub title="Your Rights under the GDPR">
            <P>
              The Company undertakes to respect the confidentiality of Your Personal Data and to guarantee You can
              exercise Your rights.
            </P>
            <P>You have the right under this Privacy Policy, and by law if You are within the EU, to:</P>
            <Ul
              items={[
                'Request access to Your Personal Data. The right to access, update or delete the information We have on You. Whenever made possible, you can access, update or request deletion of Your Personal Data directly within Your Account settings section. If you are unable to perform these actions yourself, please contact Us to assist You. This also enables You to receive a copy of the Personal Data We hold about You.',
                'Request restriction of processing. You have the right to ask Us to restrict processing of Your Personal Data in certain circumstances (for example, while We verify accuracy or consider an objection).',
                'Request correction of the Personal Data that We hold about You. You have the right to have any incomplete or inaccurate information We hold about You corrected.',
                'Object to processing of Your Personal Data. This right exists where We are relying on a legitimate interest as the legal basis for Our processing and there is something about Your particular situation, which makes You want to object to our processing of Your Personal Data on this ground. You also have the right to object where We are processing Your Personal Data for direct marketing purposes.',
                'Request erasure of Your Personal Data. You have the right to ask Us to delete or remove Personal Data when there is no good reason for Us to continue processing it.',
                'Request the transfer of Your Personal Data. We will provide to You, or to a third-party You have chosen, Your Personal Data in a structured, commonly used, machine-readable format. Please note that this right only applies to automated information which You initially provided consent for Us to use or where We used the information to perform a contract with You.',
                'Withdraw Your consent. You have the right to withdraw Your consent on using your Personal Data. If You withdraw Your consent, We may not be able to provide You with access to certain specific functionalities of the Service.',
              ]}
            />
          </Sub>

          <Sub title="Exercising of Your GDPR Data Protection Rights">
            <P>
              You may exercise Your rights of access, rectification, cancellation and opposition by contacting Us.
              Please note that we may ask You to verify Your identity before responding to such requests. If You make a
              request, We will try our best to respond to You as soon as possible. We generally respond within one
              month, and may extend by two further months where necessary, in accordance with applicable law.
            </P>
            <P>
              You have the right to complain to a Data Protection Authority about Our collection and use of Your
              Personal Data. For more information, if You are in the European Economic Area (EEA), please contact Your
              local data protection authority in the EEA.
            </P>
          </Sub>
        </Section>

        <Section title="CCPA/CPRA Privacy Notice (California Privacy Rights)">
          <P>
            This privacy notice section for California residents supplements the information contained in Our Privacy
            Policy and it applies solely to all visitors, users, and others who reside in the State of California.
          </P>

          <Sub title="Categories of Personal Information Collected">
            <P>
              We collect information that identifies, relates to, describes, references, is capable of being associated
              with, or could reasonably be linked, directly or indirectly, with a particular Consumer or Device. The
              following is a list of categories of personal information which We may collect or may have been collected
              from California residents within the last twelve (12) months.
            </P>
            <P>
              Please note that the categories and examples provided in the list below are those defined in the
              CCPA/CPRA. This does not mean that all examples of that category of personal information were in fact
              collected by Us, but reflects our good faith belief to the best of Our knowledge that some of that
              information from the applicable category may be and may have been collected. For example, certain
              categories of Personal Information would only be collected if You provided such personal information
              directly to Us.
            </P>
            {[
              { cat: 'Category A: Identifiers', examples: 'A real name, alias, postal address, unique personal identifier, online identifier, Internet Protocol address, email address, account name, driver\'s license number, passport number, or other similar identifiers.', collected: 'Yes' },
              { cat: 'Category B: Personal information categories listed in the California Customer Records statute (Cal. Civ. Code § 1798.80(e))', examples: 'A name, signature, physical characteristics or description, address, telephone number, passport number, driver\'s license or state identification card number, insurance policy number, education, employment, employment history, bank account number, credit card number, debit card number, or any other financial information, medical information, or health insurance information. Some personal information included in this category may overlap with other categories.', collected: 'Yes' },
              { cat: 'Category C: Protected classification characteristics under California or federal law', examples: 'Age (40 years or older), race, color, ancestry, national origin, citizenship, religion or creed, marital status, medical condition, physical or mental disability, sex (including gender, gender identity, gender expression, pregnancy or childbirth and related medical conditions), sexual orientation, veteran or military status, genetic information (including familial genetic information).', collected: 'No' },
              { cat: 'Category D: Commercial information', examples: 'Records of personal property, products or services purchased, obtained, or considered, or other purchasing or consuming histories or tendencies.', collected: 'No' },
              { cat: 'Category E: Biometric information', examples: 'Genetic, physiological, behavioral, and biological characteristics, or activity patterns used to extract a template or other identifier or identifying information, such as, fingerprints, faceprints, and voiceprints, iris or retina scans, keystroke, gait, or other physical patterns, and sleep, health, or exercise data.', collected: 'No' },
              { cat: 'Category F: Internet or other similar network activity', examples: 'Browsing history, search history, information on a consumer\'s interaction with a website, application, or advertisement.', collected: 'Yes' },
              { cat: 'Category G: Geolocation data', examples: 'Approximate physical location, physical location or movements.', collected: 'No' },
              { cat: 'Category H: Sensory data', examples: 'Audio, electronic, visual, thermal, olfactory, or similar information.', collected: 'No' },
              { cat: 'Category I: Professional or employment-related information', examples: 'Current or past job history or performance evaluations.', collected: 'No' },
              { cat: 'Category J: Non-public education information (per the Family Educational Rights and Privacy Act (20 U.S.C. Section 1232g, 34 C.F.R. Part 99))', examples: 'Education records directly related to a student maintained by an educational institution or party acting on its behalf, such as grades, transcripts, class lists, student schedules, student identification codes, student financial information, or student disciplinary records.', collected: 'No' },
              { cat: 'Category K: Inferences drawn from other personal information', examples: 'Profile reflecting a person\'s preferences, characteristics, psychological trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.', collected: 'No' },
              { cat: 'Category L: Sensitive personal information', examples: 'Government-issued identifying numbers, financial account details, genetic data, precise geolocation, race or ethnicity, religious or philosophical beliefs, union membership, mail, email, text messages, biometric data, health data, and sexual orientation or sex life.', collected: 'No' },
            ].map((row) => (
              <div key={row.cat} className="flex flex-col gap-1 pl-4 border-l-2 border-gray-200">
                <p className="font-dm font-semibold text-sm text-[rgba(0,0,0,0.87)]">{row.cat}</p>
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]"><span className="font-medium">Examples:</span> {row.examples}</p>
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]"><span className="font-medium">Collected:</span> {row.collected}</p>
              </div>
            ))}
            <P>Under CCPA/CPRA, Personal Information does not include:</P>
            <Ul
              items={[
                'Publicly available information from government records',
                'Deidentified or aggregated consumer information',
                'Information excluded from the CCPA/CPRA\'s scope, such as: Health or medical information covered by the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and the California Confidentiality of Medical Information Act (CMIA) or clinical trial data; Personal Information covered by certain sector-specific privacy laws, including the Fair Credit Reporting Act (FRCA), the Gramm-Leach-Bliley Act (GLBA) or California Financial Information Privacy Act (FIPA), and the Driver\'s Privacy Protection Act of 1994',
              ]}
            />
          </Sub>

          <Sub title="Sources of Personal Information">
            <P>We obtain the categories of personal information listed above from the following categories of sources:</P>
            <Ul
              items={[
                'Directly from You. For example, from the forms You complete on our Service, preferences You express or provide through our Service.',
                'Indirectly from You. For example, from observing Your activity on our Service.',
                'Automatically from You. For example, through cookies We or our Service Providers set on Your Device as You navigate through our Service.',
                'From Service Providers. For example, third-party vendors to monitor and analyze the use of our Service, or other third-party vendors that We use to provide the Service to You.',
              ]}
            />
          </Sub>

          <Sub title="Use of Personal Information">
            <P>
              We may use or disclose personal information We collect for "business purposes" or "commercial purposes"
              (as defined under the CCPA/CPRA), which may include the following examples:
            </P>
            <Ul
              items={[
                'To operate our Service and provide You with Our Service.',
                'To provide You with support and to respond to Your inquiries, including to investigate and address Your concerns and monitor and improve our Service.',
                'To fulfill or meet the reason You provided the information. For example, if You share Your contact information to ask a question about our Service, We will use that personal information to respond to Your inquiry.',
                'To respond to law enforcement requests and as required by applicable law, court order, or governmental regulations.',
                'As described to You when collecting Your personal information or as otherwise set forth in the CCPA/CPRA.',
                'For internal administrative and auditing purposes.',
                'To detect security incidents and protect against malicious, deceptive, fraudulent or illegal activity, including, when necessary, to prosecute those responsible for such activities.',
                'Other purposes consistent with the context in which the information was collected, or as otherwise disclosed to You at the time of collection.',
              ]}
            />
            <P>
              Please note that the examples provided above are illustrative and not intended to be exhaustive. For more
              details on how we use this information, please refer to the "Use of Your Personal Information" section.
            </P>
            <P>
              If We decide to collect additional categories of personal information or use the personal information We
              collected for materially different, unrelated, or incompatible purposes, We will update this Privacy
              Policy.
            </P>
          </Sub>

          <Sub title="Disclosure of Personal Information">
            <P>
              We may use or disclose and may have used or disclosed in the last twelve (12) months the following
              categories of personal information for business or commercial purposes:
            </P>
            <Ul
              items={[
                'Category A: Identifiers',
                'Category B: Personal information categories listed in the California Customer Records statute (Cal. Civ. Code § 1798.80(e))',
                'Category F: Internet or other similar network activity',
              ]}
            />
            <P>
              Please note that the categories listed above are those defined in the CCPA/CPRA. This does not mean that
              all examples of that category of personal information were in fact disclosed, but reflects our good faith
              belief to the best of our knowledge that some of that information from the applicable category may be and
              may have been disclosed.
            </P>
            <P>
              When We disclose Personal Information for a business purpose or a commercial purpose, We enter a contract
              that describes the purpose and requires the recipient to both keep that personal information confidential
              and not use it for any purpose except performing the contract.
            </P>
          </Sub>

          <Sub title="Sharing of Personal Information">
            <P>
              We may share, and have shared in the last twelve (12) months, Your personal information identified in the
              above categories with the following categories of third parties:
            </P>
            <Ul
              items={[
                'Service Providers',
                'Our affiliates',
                'Our business partners',
                'Third-party vendors to whom You or Your agents authorize Us to disclose Your personal information in connection with products or services We provide to You',
              ]}
            />
          </Sub>

          <Sub title="Sale of Personal Information">
            <P>
              As defined in the CCPA/CPRA, "sell" and "sale" mean selling, renting, releasing, disclosing,
              disseminating, making available, transferring, or otherwise communicating orally, in writing, or by
              electronic or other means, a Consumer's personal information by the Business to a third party for
              valuable consideration. This means that We may have received some kind of benefit in return for sharing
              personal information, but not necessarily a monetary benefit.
            </P>
            <P>
              We do not "sell" or "share" information as most people would commonly understand these terms – meaning We
              do not, and will not, disclose Your Personal Information in direct exchange for money or some other form
              of payment.
            </P>
            <P>
              We allow Our Service Providers to use Your personal information for the business purposes described in
              Our Privacy Policy, for activities such as advertising, marketing, and analytics, and these may be deemed
              a "sale" under CCPA/CPRA. For example, when We promote Our Service, We use Service Providers that market
              Our Service online, measure the marketing efforts, identify users that are interested in Our Service, etc.
              This is done by placing tracking technologies on our website such as cookies, tags, pixels, or similar,
              and by placing these technologies, online identifiers and online behavior information is shared with such
              vendors, including for "cross-context behavioral advertising". Under the CCPA, these practices are
              defined as "sharing" or "selling" Personal Information.
            </P>
            <P>
              We may disclose or may have disclosed in the last twelve (12) months the following categories in a manner
              that may be considered a "sale" or "sharing" as defined in CCPA/CPRA:
            </P>
            <Ul
              items={[
                'Category A: Identifiers',
                'Category B: Personal information categories listed in the California Customer Records statute (Cal. Civ. Code § 1798.80(e))',
                'Category F: Internet or other similar network activity',
              ]}
            />
          </Sub>

          <Sub title="Retention of Personal Information">
            <P>
              We retain California residents' Personal Information for as long as reasonably necessary to achieve the
              purposes described in this Privacy Policy (including the purposes disclosed in this CCPA/CPRA notice),
              taking into account: (i) how long we need the information to provide and maintain the Service and Your
              Account; (ii) whether You have requested deletion (subject to applicable exceptions); (iii) Our legal,
              tax, accounting, and regulatory obligations; (iv) security, fraud prevention, and abuse monitoring needs;
              and (v) the time periods needed to resolve disputes and enforce Our agreements.
            </P>
            <P>
              Specific retention periods for major data categories are described in the "Retention of Your Personal
              Information" section of Our Privacy Policy, and We may retain certain information longer where required
              or permitted by law (for example, to comply with recordkeeping obligations or to establish, exercise, or
              defend legal claims).
            </P>
          </Sub>

          <Sub title="Sale of Personal Information of Minors Under 16 Years of Age">
            <P>
              We do not knowingly collect personal information from minors under the age of 16 through our Service,
              although certain third party websites that we link to may do so. These third-party websites have their
              own terms of use and privacy policies and We encourage parents and legal guardians to monitor their
              children's Internet usage and instruct their children to never provide information on other websites
              without their permission.
            </P>
            <P>
              We do not sell the Personal Information of Consumers We actually know are less than 16 years of age,
              unless We receive affirmative authorization (the "right to opt-in") from the parent or guardian of a
              Consumer less than 16 years of age. Consumers who opt-in to the sale of personal information may opt-out
              of future sales at any time. To exercise the right to opt-out, You (or Your authorized representative)
              may submit a request to Us by contacting Us.
            </P>
            <P>
              If You have reason to believe that a child under the age of 16 has provided Us with personal information,
              please contact Us with sufficient detail to enable Us to delete that information.
            </P>
          </Sub>

          <Sub title="Your Rights under the CCPA/CPRA">
            <P>
              The CCPA/CPRA provides California residents with specific rights regarding their personal information. If
              You are a resident of California, You have the following rights:
            </P>
            <Ul
              items={[
                'The right to notice. You have the right to be notified which categories of Personal Information are being collected and the purposes for which the Personal Information is being used.',
                'The right to know/access. Under CCPA/CPRA, You have the right to request that We disclose information to You about Our collection, use, sale, disclosure for business purposes and share of personal information. Once We receive and confirm Your request, We will disclose to You: the categories of personal information We collected about You; the categories of sources for the personal information We collected about You; Our business or commercial purposes for collecting or selling that personal information; the categories of third parties with whom We share that personal information; the specific pieces of personal information We collected about You.',
                'The right to say no to the sale or sharing of Personal Information (opt-out). You have the right to direct Us to not sell Your personal information. To submit an opt-out request, please see the "Do Not Sell or Share My Personal Information" section or contact Us.',
                'The right to correct Personal Information. You have the right to correct or rectify any inaccurate personal information about You that We collected. Once We receive and confirm Your request, We will use commercially reasonable efforts to correct (and direct our Service Providers to correct) Your personal information, unless an exception applies.',
                'The right to limit use and disclosure of sensitive Personal Information. You have the right to request to limit the use or disclosure of certain sensitive personal information We collected about You, unless an exception applies.',
                'The right to delete Personal Information. You have the right to request the deletion of Your Personal Information under certain circumstances, subject to certain exceptions.',
                'The right not to be discriminated against. You have the right not to be discriminated against for exercising any of Your consumer\'s rights.',
              ]}
            />
          </Sub>

          <Sub title="Exercising Your CCPA/CPRA Data Protection Rights">
            <P>
              Additionally, in order to exercise any of Your rights under the CCPA/CPRA, and if You are a California
              resident, You can contact Us by email: info@zdellc.com
            </P>
            <P>
              Only You, or a person registered with the California Secretary of State that You authorize to act on Your
              behalf, may make a verifiable request related to Your personal information.
            </P>
            <P>Your request to Us must:</P>
            <Ul
              items={[
                'Provide sufficient information that allows Us to reasonably verify You are the person about whom We collected Personal Information or an authorized representative',
                'Describe Your request with sufficient detail that allows Us to properly understand, evaluate, and respond to it',
              ]}
            />
            <P>
              We will disclose and deliver the required information free of charge within 45 days of receiving Your
              verifiable request. The time period to provide the required information may be extended once by an
              additional 45 days when reasonably necessary and with prior notice.
            </P>
            <P>
              Any disclosures We provide will only cover the 12-month period preceding the verifiable request's
              receipt.
            </P>
          </Sub>

          <Sub title='Do Not Sell or Share My Personal Information'>
            <P>
              You have the right to opt-out of the "sale" of Your personal information. Once We receive and confirm a
              verifiable consumer request from You, We will stop "selling" Your Personal Information. To exercise Your
              right to opt-out, please contact Us or follow the below instructions.
            </P>
            <P>
              The Service Providers we partner with (for example, our analytics or advertising partners) may use
              technology on the Service that sells personal information as defined by the CCPA/CPRA law. If You wish to
              opt out of the use of Your Personal Information for interest-based advertising purposes and these
              potential sales as defined under CCPA/CPRA law, You may do so by following the instructions below.
            </P>
            <P>Please note that any opt out is specific to the browser You use. You may need to opt out on every browser that You use.</P>
            <P>Additionally, You can opt out of receiving ads that are personalized as served by our Service Providers by following our instructions presented on the Service:</P>
            <Ul
              items={[
                "The NAI's opt-out platform: http://www.networkadvertising.org/choices/",
                "The EDAA's opt-out platform: http://www.youronlinechoices.com/",
                "The DAA's opt-out platform: http://optout.aboutads.info/?c=2&lang=EN",
              ]}
            />
            <P>
              The opt-out will place a cookie on Your computer that is unique to the browser You use to opt out. If
              you change browsers or delete the cookies saved by Your browser, You will need to opt out again.
            </P>
            <P>Your mobile device may give You the ability to opt out of the use of information about the apps You use in order to serve You ads that are targeted to Your interests:</P>
            <Ul
              items={[
                '"Opt out of Interest-Based Ads" or "Opt out of Ads Personalization" on Android devices',
                '"Limit Ad Tracking" on iOS devices',
              ]}
            />
          </Sub>

          <Sub title='"Do Not Track" Policy as Required by California Online Privacy Protection Act (CalOPPA)'>
            <P>Our Service does not respond to Do Not Track signals.</P>
            <P>
              However, some third-party websites do keep track of Your browsing activities. If You are visiting such
              websites, You can set Your preferences in Your web browser to inform websites that You do not want to be
              tracked. You can enable or disable DNT by visiting the preferences or settings page of Your web browser.
            </P>
          </Sub>

          <Sub title="Your California Privacy Rights (California's Shine the Light law)">
            <P>
              Under California Civil Code Section 1798 (California's Shine the Light law), California residents with
              an established business relationship with Us can request information once a year about sharing their
              Personal Data with third parties for the third parties' direct marketing purposes.
            </P>
            <P>
              If you'd like to request more information under the California Shine the Light law, and if You are a
              California resident, You can contact Us using the contact information provided below.
            </P>
          </Sub>

          <Sub title="California Privacy Rights for Minor Users (California Business and Professions Code Section 22581)">
            <P>
              California Business and Professions Code Section 22581 allows California residents under the age of 18
              who are registered users of online sites, services or applications to request and obtain removal of
              content or information they have publicly posted.
            </P>
            <P>
              To request removal of such data, and if You are a California resident, You can contact Us using the
              contact information provided below, and include the email address associated with Your Account.
            </P>
            <P>
              Be aware that Your request does not guarantee complete or comprehensive removal of content or information
              posted online and that the law may not permit or require removal in certain circumstances.
            </P>
          </Sub>
        </Section>

        <Section title="Children's Privacy">
          <P>
            Our Service does not address anyone under the age of 16. We do not knowingly collect personally
            identifiable information from anyone under the age of 16. If You are a parent or guardian and You are
            aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We
            have collected Personal Data from anyone under the age of 16 without verification of parental consent, We
            take steps to remove that information from Our servers.
          </P>
          <P>
            If We need to rely on consent as a legal basis for processing Your information and Your country requires
            consent from a parent, We may require Your parent's consent before We collect and use that information.
          </P>
        </Section>

        <Section title="Links to Other Websites">
          <P>
            Our Service may contain links to other websites that are not operated by Us. If You click on a third party
            link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy
            of every site You visit.
          </P>
          <P>
            We have no control over and assume no responsibility for the content, privacy policies or practices of any
            third party sites or services.
          </P>
        </Section>

        <Section title="Changes to this Privacy Policy">
          <P>
            We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new
            Privacy Policy on this page.
          </P>
          <P>
            We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming
            effective and update the "Last updated" date at the top of this Privacy Policy.
          </P>
          <P>
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy
            are effective when they are posted on this page.
          </P>
        </Section>

        <Section title="Contact Us">
          <P>If you have any questions about this Privacy Policy, You can contact us:</P>
          <Ul items={['By email: info@zdellc.com']} />
        </Section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
